/**
 * Counts down the cart reservation banner rendered by snippets/cart-reservation-timer.
 *
 * The deadline is stored in localStorage so it survives page loads and, more importantly, the cart drawer re-render
 * that follows every cart change: each re-render creates a fresh element, which picks the running deadline back up
 * instead of starting over at the full duration.
 *
 * A reservation starts the first time the banner is shown for a non-empty cart. It is cleared when the cart empties,
 * so the next first add starts a new one. A reservation that expired long ago (the customer left and came back later,
 * or checked out without ever seeing an empty cart) is treated as stale and replaced by a fresh one.
 *
 * Once the time is up the customer restores the cart by hand: the banner shows a "Restore cart" button, a second one
 * sits in the middle of the blurred cart lines ([data-restore-trigger], sections/cart-drawer), and the checkout buttons
 * are disabled until either is clicked. Restoring starts a new reservation of the full duration. The lines stay in
 * the cart throughout; assets/custom.css only blurs them while the reservation has lapsed.
 */

const STORAGE_KEY = 'cocovaye:cart-reservation-expires-at';
const STALE_AFTER = 30 * 60 * 1000;
const RESTORE_DELAY = 900;

function readDeadline() {
  try {
    const value = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch (error) {
    // Private browsing and blocked site data both throw here. The element then keeps its own in-memory deadline.
    return null;
  }
}

function writeDeadline(deadline) {
  try {
    localStorage.setItem(STORAGE_KEY, String(deadline));
  } catch (error) {
    // See readDeadline: without storage the timer still runs, it just restarts on the next render.
  }
}

function clearDeadline() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Nothing to clear if storage is unavailable.
  }
}

function formatRemaining(milliseconds) {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

class CartReservationTimer extends HTMLElement {
  connectedCallback() {
    const duration = Number(this.dataset.duration) * 1000;
    const now = Date.now();
    let deadline = readDeadline();

    if (!deadline || now - deadline > STALE_AFTER) {
      deadline = now + duration;
      writeDeadline(deadline);
    }

    this.deadline = deadline;
    this.restoreButton = this.querySelector('[data-restore]');
    this.restoreButton?.addEventListener('click', () => this.restore());

    // The cart page and the cart drawer each have a banner: restoring in one picks up the new deadline in the other
    this.onRestored = (event) => {
      if (event.target === this) return;
      this.deadline = event.detail?.deadline ?? readDeadline() ?? this.deadline;
      this.start();
    };
    document.addEventListener('cart-reservation:restored', this.onRestored);

    this.start();
  }

  disconnectedCallback() {
    clearInterval(this.interval);
    clearTimeout(this.restoreTimeout);
    document.removeEventListener('cart-reservation:restored', this.onRestored);
  }

  start() {
    clearInterval(this.interval);
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  /** Starts a fresh reservation. The short wait lets "Restoring…" read as the cart being checked, not a toggle. */
  restore() {
    if (this.restoring) return;
    this.restoring = true;

    // The banner's button and the one over the cart lines both show "Restoring…"
    const buttons = [this.restoreButton, ...document.querySelectorAll('[data-restore-trigger]')].filter(Boolean);

    buttons.forEach((button) => {
      button.setAttribute('aria-busy', 'true');
      button.dataset.label = button.textContent;
      button.textContent = button.dataset.restoringLabel || button.textContent;
    });

    this.restoreTimeout = setTimeout(() => {
      this.deadline = Date.now() + Number(this.dataset.duration) * 1000;
      writeDeadline(this.deadline);
      this.restoring = false;

      buttons.forEach((button) => {
        button.removeAttribute('aria-busy');
        button.textContent = button.dataset.label;
      });

      this.start();
      this.dispatchEvent(new CustomEvent('cart-reservation:restored', { bubbles: true, detail: { deadline: this.deadline } }));
    }, RESTORE_DELAY);
  }

  tick() {
    const remaining = this.deadline - Date.now();
    const expired = remaining <= 0;

    this.querySelector('[data-countdown]').textContent = formatRemaining(remaining);
    this.querySelector('[data-state="active"]').hidden = expired;
    this.querySelector('[data-state="expired"]').hidden = !expired;
    if (this.restoreButton) this.restoreButton.hidden = !expired;
    this.classList.toggle('is-expired', expired);

    // Disabled rather than only greyed out, so Enter on a focused button cannot reach the checkout either
    document.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.disabled = expired;
    });

    if (expired) {
      clearInterval(this.interval);
    }
  }
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}

// The restore button over the cart lines lives outside the banner (and survives its re-render), so it is delegated
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-restore-trigger]');
  if (!trigger) return;

  const scope = trigger.closest('.drawer, .shopify-section') ?? document;
  (scope.querySelector('cart-reservation-timer') ?? document.querySelector('cart-reservation-timer'))?.restore();
});

document.addEventListener('cart:change', (event) => {
  if (event.detail?.cart?.item_count === 0) {
    clearDeadline();
  }
});
